import {
  InviteCreateDto,
  InviteCreateDtoRoleEnum,
  InviteNestedSelectDto,
  InviteRoleUpdateDtoRoleEnum,
  UserCompanyNestedSelectDtoRoleEnum,
  UserNestedSelectFromCompanyDto,
} from '@@types/api.types.ts';
import { OneColumnGrid } from '@components/pages/technology/components/one-column-grid.component';
import { TeamMemberComponent } from '@components/shared/team-member.component';
import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { useGetCompanyUsers } from '@queries/company.query';
import { useGetCurrentUser } from '@queries/users.query';
import { useState } from 'react';
import { RoleSelectDropdown, removeRole } from './company-role-select-dropdown.component';

type Invite = InviteCreateDto | InviteNestedSelectDto;
type InviteRole = InviteCreateDtoRoleEnum | InviteRoleUpdateDtoRoleEnum;
type User = UserNestedSelectFromCompanyDto;

export type TeamStepComponentProps = {
  closeModal?: () => void;
  companyId: string;
  handleInviteAdd: (invite: Invite) => Promise<void>;
  handleInviteChangeRole: (invite: Invite, role: InviteRole) => Promise<void>;
  handleInviteRemove: (invite: Invite) => Promise<void>;
  handleUserChangeRole: (user: UserNestedSelectFromCompanyDto, role: string) => Promise<void>;
  handleUserRemove: (user: User) => Promise<void>;
  invites: Invite[];
  users: UserNestedSelectFromCompanyDto[];
};

export const TeamStepComponent: React.FC<TeamStepComponentProps> = (props) => {
  const {
    companyId,
    handleInviteAdd,
    handleInviteChangeRole,
    handleInviteRemove,
    handleUserChangeRole,
    handleUserRemove,
    invites,
    users,
  } = props;

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteCreateDtoRoleEnum>(InviteCreateDtoRoleEnum.Collaborator);

  const { data: user } = useGetCurrentUser();
  const { data: companyUsers } = useGetCompanyUsers();

  const [inviteLoading, setInviteLoading] = useState(false);

  const isAdmin = Boolean(
    companyUsers?.users?.some(
      (companyUser) =>
        companyUser.id === user?.id && companyUser.userCompany?.role === UserCompanyNestedSelectDtoRoleEnum.Admin,
    ),
  );

  const domainPlaceholder = user?.email.split('@')[1] ?? 'example.com';
  const placeholderEmail = `john.doe@${domainPlaceholder}`;

  const onClickInvite = async () => {
    try {
      setInviteLoading(true);
      const newInvite = { email, role, companyId };
      await handleInviteAdd(newInvite);
      setEmail('');
    } finally {
      setInviteLoading(false);
    }
  };

  const isEmailDuplicate = invites?.some((invite) => invite.email === email);
  const isEmailValid = email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);

  const inviteOnChangeRole = (invite: Invite) => async (currentValue: string) => {
    if (currentValue === removeRole) {
      await handleInviteRemove(invite);
    } else {
      await handleInviteChangeRole(invite, currentValue as InviteRole);
    }
  };

  const userOnChangeRole = (user: User) => async (currentValue: string) => {
    if (currentValue === removeRole) {
      await handleUserRemove(user);
    } else {
      await handleUserChangeRole(user, currentValue);
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full flex-col items-start gap-2">
        <label
          htmlFor="email"
          className="font-sora text-gray-800 mb-1"
        >
          Invite Team Members
        </label>
        <div className="flex w-full flex-row items-center gap-2">
          <Input
            id="email"
            disabled={inviteLoading}
            name="email"
            type="email"
            value={email}
            placeholder={placeholderEmail}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="off"
            className="h-10 w-full rounded-lg bg-gray-100 p-3 focus:outline-none"
          />
          <div className="min-w-[150px]">
            <RoleSelectDropdown
              currentRole={role}
              disabled={inviteLoading}
              isAdmin={isAdmin}
              onChange={(currentValue) => setRole(currentValue as InviteCreateDtoRoleEnum)}
              showRemove={false}
            />
          </div>
          <Button
            onClick={onClickInvite}
            disabled={isEmailDuplicate || !isEmailValid || email.toLowerCase() === user?.email.toLowerCase()}
            loading={inviteLoading}
            variant="primary"
          >
            Invite
          </Button>
        </div>
      </div>
      {(users.length !== 0 || invites.length !== 0) && (
        <OneColumnGrid>
          {users.length !== 0 &&
            users.map((user) => (
              <TeamMemberComponent
                isAdmin={isAdmin}
                key={user.id}
                onChangeRole={userOnChangeRole(user)}
                user={user}
              />
            ))}
          {invites.length !== 0 &&
            invites
              ?.sort((a, b) => a.email.localeCompare(b.email))
              .map((invite) => (
                <div
                  key={invite.email}
                  className="bg-[#F5F5F5] rounded-xl"
                >
                  <TeamMemberComponent
                    invite={invite}
                    isAdmin={isAdmin}
                    onChangeRole={inviteOnChangeRole(invite)}
                  />
                </div>
              ))}
        </OneColumnGrid>
      )}
    </div>
  );
};
